import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, 
  MapPin, 
  TrendingUp,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import SellerPerformanceTable from './SellerPerformanceTable';
import DistrictAnalysisChart from './DistrictAnalysisChart';
import DeliveryTrendsChart from './DeliveryTrendsChart';

function Phase2Dashboard({ userId, period, customStartDate, customEndDate }) {
  const [sellerPerformance, setSellerPerformance] = useState([]);
  const [districtData, setDistrictData] = useState([]);
  const [trendsData, setTrendsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchAllData();
  }, [userId, period, customStartDate, customEndDate]);

  async function fetchAllData() {
    setLoading(true);
    setError(null);

    try {
      const params = {
        admin_user_id: userId,
        period: period
      };

      if (period === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }

      const [sellers, districts, trends] = await Promise.all([
        axios.get(`${baseUrl}/api/stats/seller-performance`, { params }),
        axios.get(`${baseUrl}/api/stats/deliveries-by-district`, { params }),
        axios.get(`${baseUrl}/api/stats/delivery-trends`, { params })
      ]);

      setSellerPerformance(sellers.data.sellers);
      setDistrictData(districts.data.districts);
      setTrendsData(trends.data.trends);
    } catch (err) {
      console.error('Error fetching Phase 2 statistics:', err);
      setError('Error al cargar las estadísticas avanzadas');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-12 h-12 text-[#B1336E] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-red-700 font-montserrat">{error}</p>
        <button
          onClick={fetchAllData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg font-montserrat hover:bg-red-700 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Seller Performance Comparison */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
          <Users className="w-6 h-6 text-[#B1336E]" />
          Comparación de Vendedores
        </h2>
        <SellerPerformanceTable sellers={sellerPerformance} />
      </div>

      {/* District Analysis */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
          <MapPin className="w-6 h-6 text-[#B1336E]" />
          Análisis por Distrito
        </h2>
        <DistrictAnalysisChart districts={districtData} />
      </div>

      {/* Delivery Trends */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-[#B1336E]" />
          Tendencias de Entregas
        </h2>
        <DeliveryTrendsChart trends={trendsData} />
      </div>
    </div>
  );
}

export default Phase2Dashboard;